import React, { useMemo, useState } from "react";
import { FileDownload, HubOutlined, ListAlt } from "@mui/icons-material";

import BomModalSwitch from "fond/project/bom/BomModalSwitch";
import { useGetPlannerLayers } from "fond/project/panels/ExtendedDesign/hooks/useGetPlannerLayers";
import { useTopologicalViewContext } from "fond/topologicalView/TopologicalViewContext";
import { Architecture, LayerIds, Project } from "fond/types";
import { AccordionStepper, AccordionStepperPane, StackedNavigationButton, useStackedNavigationContext } from "fond/widgets";

import ServiceAreaPanel from "./ServiceAreaPanel";

interface ArtefactsPanelProps {
  project: Project;
  versionId: string;
  architecture: Architecture | null;
  readOnly: boolean;
}

const ArtefactsPanel: React.FC<ArtefactsPanelProps> = ({ project, versionId, architecture, readOnly }: ArtefactsPanelProps) => {
  const { layerKeys } = useGetPlannerLayers(versionId);
  const hasServiceArea = layerKeys.includes(LayerIds.serviceArea);
  const { open } = useStackedNavigationContext();
  const [isBomModalOpen, setIsBomModalOpen] = useState(false);
  const { isAvailable: isTopologicalViewAvailable, openView: openTopologicalView } = useTopologicalViewContext();

  const accordionPanes: AccordionStepperPane[] = useMemo(
    () => [
      {
        header: "Service Area",
        id: "service-area",
        content: <ServiceAreaPanel project={project} versionId={versionId} readOnly={readOnly} />,
        isComplete: hasServiceArea,
      },
    ],
    [project, readOnly, versionId, hasServiceArea]
  );

  return (
    <>
      <AccordionStepper id={`${project.ID}-artefacts`} panes={accordionPanes} autoMove={false} variant="outlined" />
      <StackedNavigationButton
        data-testid="view-bill-of-materials-button"
        icon={<ListAlt />}
        title="Bill of materials"
        subtitle="View design bill of materials"
        sx={{ m: 0, mt: 1 }}
        onClick={() => setIsBomModalOpen(true)}
      />
      {isTopologicalViewAvailable && (
        <StackedNavigationButton
          data-testid="open-topological-view-navigation"
          icon={<HubOutlined />}
          title="Splicing view"
          subtitle="Review the topological splice diagram"
          onClick={openTopologicalView}
          sx={{ m: 0, mt: 1 }}
        />
      )}
      <StackedNavigationButton
        data-testid="export-construction-prints-button"
        icon={<FileDownload />}
        title="Construction prints"
        subtitle="Export construction prints"
        sx={{ m: 0, mt: 1 }}
        onClick={() => open("construction-prints-export")}
      />
      {isBomModalOpen && <BomModalSwitch project={project} architecture={architecture} onClose={() => setIsBomModalOpen(false)} />}
    </>
  );
};

export default ArtefactsPanel;
